<script>
  /**
   * Records with no property, and the means to attach one.
   *
   * Two failures, one job. Unmatched Tally submissions are owner-supplied facts
   * that never reached the knowledge base; unlinked conversations are the
   * cross-property leakage condition. Both are staff-only: an unlinked record
   * cannot be proven to belong to any owner.
   */
  import api from '$lib/api.js';
  import { toast } from '$lib/toast.svelte.js';
  import { authStore } from '../stores/authStore.svelte.js';
  import PageHeader from '../components/PageHeader.svelte';
  import EmptyState from '../components/EmptyState.svelte';

  let tab = $state('tally');
  let loading = $state(true);
  let error = $state(null);
  let summary = $state(null);
  let submissions = $state([]);
  let conversations = $state([]);
  let properties = $state([]);
  /** Per-row property selection, keyed `kind:id` so the two lists cannot collide. */
  let picked = $state({});
  let saving = $state(null);

  const tenantId = $derived(authStore.tenantId ?? undefined);

  async function load() {
    loading = true;
    error = null;
    try {
      const [sum, tally, convs, props] = await Promise.all([
        api.getUnlinkedSummary({ tenant_id: tenantId }),
        api.listUnmatchedTally({ tenant_id: tenantId, limit: 100 }),
        api.listUnlinkedConversations({ tenant_id: tenantId, limit: 100 }),
        api.listProperties({ tenant_id: tenantId, limit: 200 }),
      ]);
      summary = sum;
      submissions = tally.items;
      conversations = convs.items;
      properties = props.items;
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  $effect(() => { tenantId; load(); });

  async function resolve(kind, id) {
    const propertyId = picked[`${kind}:${id}`];
    if (!propertyId) return;
    saving = `${kind}:${id}`;
    try {
      if (kind === 'tally') {
        await api.resolveTallySubmission(id, Number(propertyId), { tenant_id: tenantId });
        // Extraction is queued, not done — the next sync run picks it up.
        toast.success('Submission linked. Its answers will be extracted on the next sync.');
      } else {
        const result = await api.resolveConversationProperty(id, Number(propertyId),
          { tenant_id: tenantId });
        const n = result.auto_responses_relinked;
        toast.success(
          n ? `Conversation linked, and ${n} auto-response${n === 1 ? '' : 's'} with it.`
            : 'Conversation linked.'
        );
      }
      delete picked[`${kind}:${id}`];
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      saving = null;
    }
  }

  const tabs = $derived([
    { id: 'tally', label: `Tally submissions (${summary?.tally_submissions ?? 0})` },
    { id: 'conversations', label: `Conversations (${summary?.conversations ?? 0})` },
  ]);

  function when(value) {
    return value ? new Date(value).toLocaleDateString() : '—';
  }
</script>

<PageHeader title="Unlinked records"
            subtitle="Things we could not attach to a property automatically." />

{#if error}
  <p class="p-card p-4 text-sm" style="color: var(--p-danger)">{error}</p>
{:else}
  <div class="flex gap-1 border-b" style="border-color: var(--p-border)" role="tablist">
    {#each tabs as t}
      <button type="button" role="tab" aria-selected={tab === t.id}
              class="-mb-px border-b-2 px-3 py-2 text-sm"
              style={tab === t.id
                ? 'border-color: var(--p-brand); color: var(--p-brand); font-weight: 600'
                : 'border-color: transparent; color: var(--p-text-muted)'}
              onclick={() => (tab = t.id)}>
        {t.label}
      </button>
    {/each}
  </div>

  {#if tab === 'tally'}
    <p class="mb-4 mt-4 text-sm p-muted">
      A property manager filled out the form, but address matching could not tell which
      property it was — so none of their answers reached the knowledge base. Linking one
      queues it for extraction on the next sync.
    </p>

    {#if loading}
      <p class="p-card p-6 text-sm p-muted">Loading…</p>
    {:else if !submissions.length}
      <EmptyState icon="fa-circle-check" title="Nothing unmatched"
                  message="Every Tally submission is attached to a property." />
    {:else}
      <div class="space-y-3">
        {#each submissions as item (item.id)}
          <div class="p-card p-4">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div class="min-w-0">
                <p class="text-sm font-medium">
                  {item.respondent_name ?? `Submission #${item.id}`}
                </p>
                <p class="mt-0.5 text-xs p-faint">
                  {item.form_role ?? 'unknown form'} · submitted {when(item.submitted_at)}
                  {#if item.candidate_property_name}
                    · best guess was {item.candidate_property_name}
                  {/if}
                </p>
              </div>
              <span class="text-xs p-faint">#{item.id}</span>
            </div>

            {#if item.address_answers.length}
              <div class="mt-3 rounded-lg px-3 py-2 text-xs"
                   style="background-color: var(--p-bg-subtle, rgba(120,120,140,.08))">
                <p class="p-faint">Text the matcher tried:</p>
                <ul class="mt-1 space-y-0.5">
                  {#each item.address_answers as answer}
                    <li class="font-mono">{answer}</li>
                  {/each}
                </ul>
              </div>
            {:else}
              <p class="mt-3 text-xs p-faint">
                This form had no usable free-text answer to match on.
              </p>
            {/if}

            <div class="mt-3 flex flex-wrap items-center gap-2">
              <select class="p-input w-auto flex-1" bind:value={picked[`tally:${item.id}`]}
                      aria-label="Property for submission {item.id}">
                <option value={undefined}>Choose a property…</option>
                {#each properties as property}
                  <option value={property.id}>{property.name}</option>
                {/each}
              </select>
              <button type="button" class="p-btn p-btn-primary"
                      disabled={!picked[`tally:${item.id}`] || saving === `tally:${item.id}`}
                      onclick={() => resolve('tally', item.id)}>
                Link
              </button>
            </div>
          </div>
        {/each}
      </div>
    {/if}
  {:else}
    <p class="mb-4 mt-4 text-sm p-muted">
      Hostaway sent no listing for these conversations, so any reply drafted against them had
      no property facts to check itself against. Linking one also re-attaches the
      auto-responses generated from it.
    </p>

    {#if loading}
      <p class="p-card p-6 text-sm p-muted">Loading…</p>
    {:else if !conversations.length}
      <EmptyState icon="fa-circle-check" title="Nothing unlinked"
                  message="Every conversation is attached to a property." />
    {:else}
      <div class="space-y-3">
        {#each conversations as item (item.id)}
          <div class="p-card p-4">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p class="text-sm font-medium">{item.guest_name ?? 'Unknown guest'}</p>
                <p class="mt-0.5 text-xs p-faint">
                  Hostaway #{item.hostaway_conversation_id}
                  · {item.message_count} message{item.message_count === 1 ? '' : 's'}
                  · last {when(item.last_message_at)}
                </p>
              </div>
              {#if item.auto_response_count > 0}
                <span class="rounded-full px-2 py-0.5 text-xs"
                      style="background-color: var(--p-warn-bg, rgba(180,120,0,.12))">
                  {item.auto_response_count} auto-response{item.auto_response_count === 1 ? '' : 's'}
                  without property context
                </span>
              {/if}
            </div>

            <div class="mt-3 flex flex-wrap items-center gap-2">
              <select class="p-input w-auto flex-1" bind:value={picked[`conv:${item.id}`]}
                      aria-label="Property for conversation {item.id}">
                <option value={undefined}>Choose a property…</option>
                {#each properties as property}
                  <option value={property.id}>{property.name}</option>
                {/each}
              </select>
              <button type="button" class="p-btn p-btn-primary"
                      disabled={!picked[`conv:${item.id}`] || saving === `conv:${item.id}`}
                      onclick={() => resolve('conv', item.id)}>
                Link
              </button>
            </div>
          </div>
        {/each}
      </div>
    {/if}
  {/if}
{/if}
